import { flexRender } from '@tanstack/react-table'
import { Table } from '../Table'
import { useDataTableContext } from './DataTableContext'
import { scopedTranslation } from '@frontend/utils/I18n'
import cn from '@components/utils/cn'

const t = scopedTranslation('shared')

// TanStack React Table defaults column size to 150 when not explicitly set.
// We treat this as "auto" and let the browser size the column naturally.
export const TANSTACK_DEFAULT_COLUMN_SIZE = 150

// Module-level so React calls it only when the row mounts or becomes highlighted, not on every render.
// 'nearest' leaves a row that is already on screen where it is.
const scrollRowIntoView = (row: HTMLTableRowElement | null) => row?.scrollIntoView({ block: 'nearest' })

type DataTableContentProps = {
  caption?: string
  className?: string
  emptyState?: React.ReactNode
  highlightedRowId?: string | null
}

export function DataTableContent({ caption, className, emptyState, highlightedRowId }: DataTableContentProps) {
  const table = useDataTableContext()

  return (
    <div className={cn('tw:rounded-md tw:border', className)}>
      <Table.Root>
        {caption && <Table.Caption className="tw:sr-only">{caption}</Table.Caption>}
        <Table.Header>
          {table.getHeaderGroups().map((headerGroup) => (
            <Table.Row key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const meta = header.column.columnDef.meta
                return (
                  <Table.Head
                    key={header.id}
                    className={cn(
                      'tw:whitespace-nowrap',
                      meta?.centerContent && 'tw:text-center',
                      meta?.alignRight && 'tw:text-right',
                      meta?.hideOnMobile && 'tw:hidden tw:md:table-cell',
                      meta?.stickyRight && 'tw:sticky tw:right-0 tw:bg-white'
                    )}
                    style={{
                      width: header.getSize() !== TANSTACK_DEFAULT_COLUMN_SIZE ? `${header.getSize()}px` : undefined,
                      minWidth: header.column.columnDef.minSize ? `${header.column.columnDef.minSize}px` : undefined,
                    }}
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </Table.Head>
                )
              })}
            </Table.Row>
          ))}
        </Table.Header>
        <Table.Body>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => {
              const highlighted = row.id === highlightedRowId
              return (
                <Table.Row
                  key={row.id}
                  ref={highlighted ? scrollRowIntoView : undefined}
                  data-highlighted={highlighted || undefined}
                  className={cn(highlighted && 'tw:bg-primary-50 tw:hover:bg-primary-50')}
                >
                  {row.getVisibleCells().map((cell) => {
                    const meta = cell.column.columnDef.meta
                    return (
                      <Table.Cell
                        key={cell.id}
                        className={cn(
                          !meta?.allowWrap && 'tw:whitespace-nowrap',
                          meta?.centerContent && 'tw:text-center',
                          meta?.alignRight && 'tw:text-right',
                          meta?.hideOnMobile && 'tw:hidden tw:md:table-cell',
                          meta?.stickyRight && 'tw:sticky tw:right-0 tw:bg-white',
                          meta?.stickyRight && highlighted && 'tw:bg-primary-50'
                        )}
                        style={{
                          width:
                            cell.column.getSize() !== TANSTACK_DEFAULT_COLUMN_SIZE
                              ? `${cell.column.getSize()}px`
                              : undefined,
                          minWidth: cell.column.columnDef.minSize ? `${cell.column.columnDef.minSize}px` : undefined,
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Table.Cell>
                    )
                  })}
                </Table.Row>
              )
            })
          ) : (
            <Table.Row>
              <Table.Cell colSpan={table.getAllColumns().length} className="tw:h-24 tw:text-center">
                {emptyState ?? t('search.no_results')}
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table.Root>
    </div>
  )
}
